import Box from "@mui/material/Box";
import {Stack} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {AggregationReservationListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {
    ReservationAggregationService
} from "crqlar-react-common/dist/services/aggregation/reservationAggregation.service";
import {formatFullDateTime, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useReservationFilterOptions} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import type {DailyStayStatus} from "crqlar-react-common/dist/models/dailyList/dailyList.model";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTablePrint,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {ReservationStatusElement} from "../../common/uiElements/ReservationStatusElement";
import {NotesElement} from "../../common/uiElements/NotesElement";
import {AgeCategoriesElement} from "../../common/uiElements/AgeCategoriesElement";
import {AliasAgeCategoriesElement} from "../../common/uiElements/AliasAgeCategoriesElement";
import {StayStatusElement} from "../../common/uiElements/StayStatusElement";
import {AggregationResourceElement} from "../../common/uiElements/ResourceElement";

interface ProfileStayReservationListProps {
    profileId: string;
}

// Derive a temporal stay status from the stay's start/end dates relative to today, so the list
// can render <StayStatusElement/> (the reservation item carries no stay-status field of its own).
const deriveStayStatus = (start?: Date, end?: Date): DailyStayStatus | undefined => {
    const today = startOfDay(new Date()).getTime();
    const startDay = start ? startOfDay(start).getTime() : undefined;
    const endDay = end ? startOfDay(end).getTime() : undefined;
    if (startDay !== undefined && startDay === today) return 'CHECKING_IN';
    if (endDay !== undefined && endDay === today) return 'CHECKING_OUT';
    if (startDay !== undefined && endDay !== undefined && startDay < today && today < endDay) return 'STAYING';
    if (startDay !== undefined && startDay > today) return 'UPCOMING';
    return undefined;
};

const ProfileStayReservationList = ({profileId}: ProfileStayReservationListProps) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const filterOptionsQuery = useReservationFilterOptions();
    const ageCategories = useAllAgeCategories();
    const allSectors = useAllSectors();

    // Stays = accommodation reservations, so scope the query to the accommodation sectors.
    const accommodationSectorIds = allSectors
        .filter(sector => sector.sectorType === SectorType.ACCOMMODATION)
        .map(sector => sector.id)
        .filter((id): id is string => !!id);

    const columns = useMemo<ColumnDef<AggregationReservationListItem>[]>(() => [
        {
            id: 'status',
            header: t('Status'),
            accessorFn: reservation => reservation.status,
            cell: ({row}) => <ReservationStatusElement status={row.original.status}/>,
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'status',
                printable: true,
                printDefault: true,
                printCell: row => <ReservationStatusElement status={row.original.status} asPrint/>
            }
        },
        {
            id: 'stayStatus',
            header: t('Stay'),
            accessorFn: reservation => deriveStayStatus(reservation.startDate, reservation.endDate),
            cell: ({row}) => (
                <StayStatusElement
                    stayStatus={deriveStayStatus(row.original.startDate, row.original.endDate)}
                    startDate={row.original.startDate}
                    endDate={row.original.endDate}
                />
            ),
            size: 150,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <StayStatusElement
                        stayStatus={deriveStayStatus(row.original.startDate, row.original.endDate)}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'start',
            header: t('Start'),
            accessorFn: reservation => reservation.startDate,
            cell: ({row}) => row.original.startDate ? formatFullDateTime(row.original.startDate) : '',
            size: 180,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'expectedStartDate',
                printable: true,
                printDefault: true,
                printCell: row => formatFullDateTime(row.original.startDate)
            }
        },
        {
            id: 'end',
            header: t('End'),
            accessorFn: reservation => reservation.endDate,
            cell: ({row}) => row.original.endDate ? formatFullDateTime(row.original.endDate) : '',
            size: 180,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'expectedEndDate',
                printable: true,
                printDefault: true,
                printCell: row => formatFullDateTime(row.original.endDate)
            }
        },
        {
            id: 'service',
            header: t('Service'),
            accessorFn: reservation => reservation.service?.name,
            cell: ({row}) => row.original.service?.name ?? '',
            size: 220,
            meta: {defaultActive: true, printable: true, printDefault: true}
        },
        {
            id: 'resources',
            header: t('Resources'),
            accessorFn: reservation => reservation.resources.map(resource => resource.name).filter(Boolean),
            cell: ({row}) => (
                <Stack direction={'row'} gap={0.5} flexWrap={'wrap'}>
                    {row.original.resources.map(resource => (
                        <AggregationResourceElement key={resource.id} resource={resource}/>
                    ))}
                </Stack>
            ),
            size: 240,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'guestCount',
            header: t('Guest count abbreviation'),
            accessorFn: reservation => reservation.guestCount,
            cell: ({getValue}) => <Box>{getValue<number>()}</Box>,
            size: 120,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'guests',
            header: t('Age categories'),
            accessorFn: reservation => reservation.guests.adults + reservation.guests.children,
            cell: ({row}) => (
                <AgeCategoriesElement
                    ageCategories={ageCategories}
                    computedAgeCategories={row.original.guests}
                />
            ),
            size: 150,
            meta: {
                defaultActive: true,
                printable: true,
                printCell: row => (
                    <AgeCategoriesElement
                        ageCategories={ageCategories}
                        computedAgeCategories={row.original.guests}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'aliasAgeCategories',
            header: `${t('Age categories')} (${t('Alias')})`,
            accessorFn: reservation => reservation.aliasAgeCategories,
            cell: ({row}) => (
                <AliasAgeCategoriesElement
                    aliasAgeCategories={row.original.aliasAgeCategories}
                    ageCategories={ageCategories}
                />
            ),
            size: 150,
            meta: {
                defaultActive: false,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AliasAgeCategoriesElement
                        aliasAgeCategories={row.original.aliasAgeCategories}
                        ageCategories={ageCategories}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'code',
            header: t('Code'),
            accessorFn: reservation => reservation.code,
            cell: ({row}) => row.original.code ?? '',
            size: 130,
            meta: {defaultActive: false, isServerSortable: true, serverSortField: 'code'}
        },
        {
            id: 'sector',
            header: t('Sector'),
            accessorFn: reservation => reservation.sector?.name,
            cell: ({row}) => row.original.sector?.name ?? '',
            size: 140,
            meta: {defaultActive: false}
        },
        {
            id: 'notes',
            header: t('Notes'),
            accessorFn: reservation => reservation.notes,
            cell: ({row}) => <NotesElement notes={row.original.notes}/>,
            size: 260,
            meta: {
                defaultActive: false,
                printable: true
            }
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: reservation => reservation.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        }
    ], [ageCategories, t]);

    const controller = useServerTable<AggregationReservationListItem>({
        tableId: 'system-profile-stays',
        columns,
        getRowId: reservation => reservation.id,
        onRowClick: reservation => navigate(`/system/reservations/${reservation.id}`),
        queryDependencies: [profileId, accommodationSectorIds.join(',')],
        queryFn: ({pagination, searchQuery, filterRules, sorting}) => ReservationAggregationService.listReservations({
            query: searchQuery || undefined,
            profileIds: [profileId],
            sectorIds: accommodationSectorIds,
            advancedFilter: filterRules.length ? {
                filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
            } : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            }
        })
    });

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', height: '70vh', minHeight: 0}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar>
                        <ServerTableSearch placeholder={t('Search stays')}/>
                        <ServerTableSort/>
                        <ServerTableFilter
                            options={filterOptionsQuery.data ?? []}
                            disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                        />
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                        <ServerTablePrint title={t('Stays')}/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </Box>
    );
};

export default ProfileStayReservationList;
